import { Injectable } from '@angular/core';

import * as sdk from '@mycure/sdk';
import { MycureSDK } from '../../service';
import * as _ from 'lodash';

import { Logger } from './../../logger/logger.service';
const log = new Logger('@mycure/mc-services');

@Injectable({
  providedIn: 'root'
})

export class McWhTransactionService {

  constructor(
    private sdk: MycureSDK,
  ) {

  }

  async loadAdjustmentOrders(organizationId: string) {
    const query = { warehouse: organizationId, type: 'adjustment', $sort: { createdAt: -1 } };
    const res = await this.sdk.warehouse.transactions().find(query);
    let orders: any[] = res.items;
    // create temp ids
    orders = orders.map((item: any) => this.createId(item));
    return orders;
  }

  async loadReturnOrders(organizationId: string) {
    const query = { warehouse: organizationId, type: 'return', $sort: { createdAt: -1 } };
    const res = await this.sdk.warehouse.transactions().find(query);
    let orders: any[] = res.items;
    // create temp ids
    orders = orders.map((item: any) => this.createId(item));
    return orders;
  }

  async loadDispenseOrders(organizationId: string) {
    const query = { type: 'sales', warehouse: organizationId, $sort: { createdAt: -1 }, customerType: 'org-member' };
    const res = await this.sdk.warehouse.transactions().find(query);
    let orders: any[] = res.items;
    // create temp ids
    orders = orders.map((item: any) => this.createId(item));
    return orders;
  }

  loadDispenseOrder(orderId: string) {
    const query = { type: 'sales', id: orderId };
    return this.sdk.warehouse.transactions().findOne(query);
  }

  async loadPoFromSuppliers(organizationId: string) {
    const query: any = {
      type: 'purchase', warehouse: organizationId,
      supplierType: 'wh-supplier',
      $sort: { createdAt: -1 },
      $populate: {
        supplier: {
          service: 'wh-suppliers', key: 'supplier', method: 'findOne', idField: 'id'
        }
      }
    };

    let res = await this.sdk.warehouse.transactions().find(query);
    let orders: any[] = res.items;

    // map populated items
    orders = orders.map((item: any) => {
      let totalUnits: number = 0;
      item = this.createId(item)

      if (item && item.items
        && item.items.length != 0) {

        item.items.map((item: any) => {
          if (item.quantity) {
            // increment value
            const value = parseInt(item.quantity);
            totalUnits = totalUnits += value;
          }
        });
      }

      return {
        ..._.omit(item, ['$populated']),
        _totalUnits: totalUnits,
        _supplier: item.$populated.supplier || {}
      };
    });

    return orders;
  }

  /**
   * Load Purchase Order and related receiving orders
   * @param orderId 
   */
  async loadPoReceivingOrders(orderId: string) {
    // load receiving items for that order
    const query1: any = { type: 'receiving', transaction: orderId };
    const orders = await this.sdk.warehouse.transactions().find(query1);
    const receivingOrders = orders.items;

    const query2: any = {
      id: orderId,
      $populate: {
        items: {
          service: 'wh-stocks', key: 'items', extractKey: 'sku',
          method: 'find', idField: 'variant', methodOps: '$in',
        }
      },
    };

    // load order
    let order: any = await this.sdk.warehouse.transactions().findOne(query2);
    if (!order) throw new Error('No orders found');

    order = {
      ..._.omit(order, ['$populated']),
      _items: order.$populated.items || []
    };

    // remove all un necessary stocks count
    // trick to filter all stock related to the receiving
    // facility only.
    if (order._items && order._items.length != 0) {
      order._items = order._items.filter((item: any) => item.warehouse == order.warehouse);

      // add stock items to order items
      order.items = order.items.map((item: any) => {
        const stock = _.findLast(order._items, (stock: any) => stock.variant == item.sku);
        if (stock) item._stock = stock
        return item;
      });
    }

    return { order, receivingOrders };
  }

  /**
   * Load Stock Request Order and it's related Receiving & Transfer Orders
   * @param orderId 
   * @param supplierType - supplier(where to deduct items), warehouse(where to add items)
   */
  async loadStockRequestRelatedOrders(orderId: string, supplierType: string = 'supplier', loadTransfers: boolean = false) {
    // load all transfer orders for that transaction
    let transferOrders: any[] = [];
    if (loadTransfers) {
      const transferQuery: any = { type: 'transfer', transaction: orderId };
      const orders1 = await this.sdk.warehouse.transactions().find(transferQuery);
      transferOrders = orders1.items;
    }

    // load receiving items for loaded transfer transactions
    const transferOrderIds: string[] = transferOrders.map((item: any) => item.id);
    const receivingQuery: any = { type: 'receiving', transaction: { $in: transferOrderIds } };
    const orders2 = await this.sdk.warehouse.transactions().find(receivingQuery);
    const receivingOrders = orders2.items;

    // loaded to get warehouse id
    const key: string = supplierType;
    const query3: any = {
      id: orderId,
      $populate: {
        supplier: {
          service: 'organizations', key,
          method: 'findOne', idField: 'id'
        },
        items: {
          service: 'wh-stocks', key: 'items', extractKey: 'sku',
          method: 'find', idField: 'variant', methodOps: '$in',
        }
      },
    };

    // load order
    let order: any = await this.sdk.warehouse.transactions().findOne(query3);
    if (!order) throw new Error('No orders found');

    order = {
      ..._.omit(order, ['$populated']),
      _supplier: order.$populated.supplier || {},
      _items: order.$populated.items || []
    };

    // remove all un necessary stocks count
    // trick to filter all stock related to the receiving
    // facility only.
    if (order._items && order._items.length != 0) {
      order._items = order._items.filter((item: any) => item.warehouse == order.warehouse);

      // add stock items to order items
      order.items = order.items.map((item: any) => {
        const stock = _.findLast(order._items, (stock: any) => stock.variant == item.sku);
        if (stock) item._stock = stock
        return item;
      });
    }

    return { order, receivingOrders, transferOrders };
  }

  /**
   * Load Stock Requests created by a organization (usually child org)
   * @param orgId 
   */
  async loadStockRequests(organizationId: string) {
    const query = {
      $populate: {
        supplier: {
          service: 'organizations', key: 'supplier',
          method: 'findOne', idField: 'id'
        }
      },
      type: 'purchase', supplierType: 'organization',
      $sort: { createdAt: -1 },
      warehouse: organizationId
    };

    const res = await this.sdk.warehouse.transactions().find(query);
    let orders = res.items;

    // map populated items
    orders = orders.map((item: any) => {
      let totalUnits: number = 0;
      if (item && item.items
        && item.items.length != 0) {

        item.items.map((item: any) => {
          if (item.quantity) {
            // increment value
            const value = parseInt(item.quantity);
            totalUnits = totalUnits += value;
          }
        });
      }

      return {
        ..._.omit(item, ['$populated']),
        _totalUnits: totalUnits,
        _supplier: item.$populated.supplier || {}
      };
    });

    return orders;
  }

  /**
   * Load Transfer Orders received by a organization
   * @param organizationId 
   */
  async loadTransferOrders(organizationId: string) {
    const query = {
      $populate: {
        supplier: {
          service: 'organizations', key: 'warehouse',
          method: 'findOne', idField: 'id'
        }
      },
      type: 'purchase', supplierType: 'organization',
      $sort: { createdAt: -1 },
      supplier: organizationId
    };

    const res = await this.sdk.warehouse.transactions().find(query);
    let orders = res.items;

    // map populated items
    orders = orders.map((item: any) => {
      let totalUnits: number = 0;

      if (item && item.items && item.items.length != 0) {
        item.items.map((item: any) => {
          if (item.quantity) {
            // increment value
            const value = parseInt(item.quantity);
            totalUnits = totalUnits += value;
          }
        });
      }

      item = this.createId(item);

      return {
        ..._.omit(item, ['$populated']),
        _totalUnits: totalUnits,
        _supplier: item.$populated.supplier || {}
      };
    });

    return orders;
  }

  /**
   * Create an adjustment order
   * @param data 
   */
  async createAdjustmentOrder(data: any) {
    let _order: any = _.pick(data,
      ['warehouse', 'type', 'finalize', 'reason', 'items']
    );

    _order.items = data.items.map((item: any) => {
      return _.pick(item, ['adjustment', 'sku']);
    });

    return this.sdk.warehouse.transactions().create(_order);
  }

  /**
   * Create a purchase order transaction
   * @param data 
   */
  async createPurchaseOrder(data: any) {
    let _order: any = _.pick(data,
      ['warehouse', 'type', 'finalize', 'supplier', 'supplierType', 'reason', 'items', 'expiresAt']
    );

    _order.items = _order.items.map((item: any) => {
      return _.pick(item, ['price', 'quantity', 'sku', 'taxComplication', 'tax', 'taxCode']);
    });

    await this.sdk.warehouse.transactions().create(_order);
  }

  async createSalesOrder(data: any) {
    let _order: any = _.pick(data,
      ['warehouse', 'type', 'finalize', 'customerType', 'customer', 'items']
    );

    _order.items = _order.items.map((item: any) => {
      return _.pick(item, ['price', 'quantity', 'sku', 'taxComplication', 'tax', 'taxCode']);
    });

    _order.invoice = true;

    return this.sdk.warehouse.transactions().create(_order);
  }

  async createDispenseOrder(data: any) {
    let _order: any = _.pick(data,
      ['warehouse', 'type', 'finalize', 'customerType', 'items']
    );

    let trackingItems: any[] = [];

    _order.items = data.items.map((item: any) => {
      if (item.tracking == true) {
        // make it positive
        const totalTrackingItems = Math.abs(item.quantity);
        const tracking: any = { sku: item.sku, status: 'in-use', quantity: totalTrackingItems, warehouse: _order.warehouse };
        trackingItems.push(tracking);
      }

      return _.pick(item, ['price', 'quantity', 'sku', 'taxComplication', 'tax', 'taxCode']);
    });

    // create a tracking items
    if (trackingItems.length != 0) {
      await this.sdk.warehouse.trackers().create(trackingItems);
    }

    return this.sdk.warehouse.transactions().create(_order);
  }

  async createReturnOrder(data: any) {
    let _order: any = _.pick(data,
      ['warehouse', 'type', 'finalize', 'customerType', 'customer', 'items', 'reason']
    );

    _order.items = _order.items.map((item: any) => {
      return _.pick(item, ['price', 'quantity', 'sku', 'taxComplication', 'tax', 'taxCode']);
    });

    return this.sdk.warehouse.transactions().create(_order);
  }

  /**
   * Create a receiving purchase order transaction
   * @param data 
   */
  async createReceiveOrder(data: any) {
    let _order: any = _.pick(data,
      ['type', 'finalize', 'transaction', 'items', 'warehouse']
    );

    if (_order.items) {
      // get all items that are not completed
      _order.items = _order.items.filter((item: any) => item._completed == false);
      _order.items = _order.items.map((item: any) => {
        return _.pick(item,
          ['price', 'quantity', 'sku', 'taxComplication', 'tax', 'taxCode']
        );
      });

      // do not allow to receive a 0 quantity
      _order.items.map((item: any) => {
        if (item.quantity == 0) {
          throw new Error('invalid input');
        }
      });
    }

    await this.sdk.warehouse.transactions().create(_order);
  }

  /**
   * Create a transfer order transaction
   * @param data 
   */
  async createTransferOrder(data: any) {
    let _order: any = {
      type: 'transfer',
      finalize: true,
      warehouse: data.warehouse,
      source: data.supplier,
      destination: data.warehouse,
      transaction: data.transaction // link order
    }

    if (data.items) {
      _order.items = data.items.map((item: any) => {
        return _.pick(item,
          ['price', 'quantity', 'sku', 'taxComplication', 'tax', 'taxCode']
        );
      });
    }

    await this.sdk.warehouse.transactions().create(_order);
  }

  /**
   * Approve a transaction
   * @param data 
   */
  async createApprovedOrder(data: any) {
    const update = { approve: true };
    await this.sdk.warehouse.transactions().update(data.transaction, update);
  }

  /**
   * Remove a transaction item
   * @param id 
   */
  removeTransaction(id: string) {
    return this.sdk.warehouse.transactions().remove(id);
  }

  /**
   * Create a receiving transaction for a transfer transaction
   * @param order - transfer order 
   */
  receiveTransferOrder(order: any) {
    let receivingOrder = {
      type: 'receiving',
      finalize: true,
      transaction: order.id,
      items: order.items,
      warehouse: order.warehouse
    };

    if (order.items) {
      receivingOrder.items = order.items.map((item: any) => {
        return _.pick(item,
          ['price', 'quantity', 'sku', 'taxComplication', 'tax', 'taxCode']
        );
      });
    }

    return this.sdk.warehouse.transactions().create(receivingOrder);
  }

  /**
   * Create a temporary id
   * @param order 
   */
  private createId(order: any) {
    if (order && order.id) {
      const prefix = order.id.substr(0, 3).toUpperCase();
      const suffix = order.id.substr(-10).toUpperCase();
      const _id = `${prefix}-${suffix}`;
      return { _id, ...order };
    }

    return order;
  }
}
